import ArticleIcon from "@mui/icons-material/Article";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { ReactNode } from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { Utilisateur } from "../api/hooks";
import { useDeconnexion } from "../api/hooks";

const LARGEUR_BARRE = 264;

type Entree = {
  libelle: string;
  chemin: string;
  icone: ReactNode;
  /** Les écrans encore à écrire figurent au menu, grisés, plutôt qu'absents :
   *  la structure de l'admin reste lisible sans promettre un lien qui casse. */
  aVenir?: boolean;
};

const ENTREES: Entree[] = [
  { libelle: "Tableau de bord", chemin: "/", icone: <DashboardIcon /> },
  { libelle: "Demandes", chemin: "/leads", icone: <PersonSearchIcon /> },
  { libelle: "Clients", chemin: "/clients", icone: <PersonSearchIcon /> },
  { libelle: "Chantiers", chemin: "/chantiers", icone: <PermMediaIcon /> },
  { libelle: "Prestations", chemin: "/contenu/prestations", icone: <ArticleIcon /> },
  { libelle: "Communes", chemin: "/contenu/communes", icone: <ArticleIcon /> },
  { libelle: "Réalisations", chemin: "/contenu/realisations", icone: <ArticleIcon /> },
  { libelle: "Questions fréquentes", chemin: "/contenu/faq", icone: <ArticleIcon /> },
  { libelle: "Avis", chemin: "/contenu/avis", icone: <ArticleIcon /> },
  { libelle: "Référencement", chemin: "/seo", icone: <ArticleIcon /> },
  { libelle: "Réglages", chemin: "/reglages", icone: <ArticleIcon /> },
  { libelle: "Médiathèque", chemin: "/medias", icone: <PermMediaIcon /> },
  { libelle: "Publication", chemin: "/publication", icone: <CloudUploadIcon /> },
];

type ProprietesLayout = {
  utilisateur: Utilisateur;
  children: ReactNode;
};

export function Layout({ utilisateur, children }: ProprietesLayout) {
  const theme = useTheme();
  const surGrandEcran = useMediaQuery(theme.breakpoints.up("md"));
  const [tiroirOuvert, setTiroirOuvert] = useState(false);
  const emplacement = useLocation();
  const naviguer = useNavigate();
  const deconnexion = useDeconnexion();

  function seDeconnecter() {
    // Même si l'appel échoue, on repart vers la connexion : le cookie est
    // peut-être déjà mort côté serveur, rester sur l'admin n'aiderait personne.
    deconnexion.mutate(undefined, {
      onSettled: () => naviguer("/connexion", { replace: true }),
    });
  }

  const navigation = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ px: 3 }}>
        <Box>
          <Typography variant="h3" component="p" sx={{ color: "secondary.main", lineHeight: 1.2 }}>
            Delsa
          </Typography>
          <Typography variant="caption" sx={{ color: "common.white", opacity: 0.75 }}>
            Administration
          </Typography>
        </Box>
      </Toolbar>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <List component="nav" aria-label="Sections de l'administration" sx={{ py: 1 }}>
        {ENTREES.map((entree) => {
          const actif = emplacement.pathname === entree.chemin;

          return (
            <ListItem key={entree.chemin} disablePadding>
              <ListItemButton
                selected={actif}
                disabled={entree.aVenir}
                aria-current={actif ? "page" : undefined}
                onClick={() => {
                  naviguer(entree.chemin);
                  setTiroirOuvert(false);
                }}
                sx={{
                  color: "common.white",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(212,175,55,0.18)",
                    borderLeft: "3px solid",
                    borderColor: "secondary.main",
                  },
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              >
                <ListItemIcon sx={{ color: actif ? "secondary.main" : "rgba(255,255,255,0.7)" }}>
                  {entree.icone}
                </ListItemIcon>
                <ListItemText
                  primary={entree.libelle}
                  secondary={entree.aVenir ? "Bientôt" : undefined}
                  slotProps={{
                    secondary: { sx: { color: "rgba(255,255,255,0.55)" } },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh", backgroundColor: "background.default" }}>
      <AppBar
        position="fixed"
        color="primary"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${LARGEUR_BARRE}px)` },
          ml: { md: `${LARGEUR_BARRE}px` },
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Ouvrir le menu"
            onClick={() => setTiroirOuvert(true)}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {ENTREES.find((entree) => entree.chemin === emplacement.pathname)?.libelle ??
              "Administration"}
          </Typography>

          <Typography variant="body2" sx={{ mr: 2, display: { xs: "none", sm: "block" } }}>
            {utilisateur.nom || utilisateur.email}
          </Typography>

          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={seDeconnecter}
            disabled={deconnexion.isPending}
          >
            {deconnexion.isPending ? "Déconnexion…" : "Se déconnecter"}
          </Button>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: LARGEUR_BARRE }, flexShrink: { md: 0 } }}>
        {surGrandEcran ? (
          <Drawer
            variant="permanent"
            open
            slotProps={{
              paper: {
                sx: {
                  width: LARGEUR_BARRE,
                  boxSizing: "border-box",
                  backgroundColor: "primary.main",
                  borderRight: "none",
                },
              },
            }}
          >
            {navigation}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={tiroirOuvert}
            onClose={() => setTiroirOuvert(false)}
            slotProps={{
              // Le tiroir mobile se rouvre souvent : le garder monté évite de
              // reconstruire la navigation à chaque ouverture.
              root: { keepMounted: true },
              paper: {
                sx: {
                  width: LARGEUR_BARRE,
                  boxSizing: "border-box",
                  backgroundColor: "primary.main",
                },
              },
            }}
          >
            {navigation}
          </Drawer>
        )}
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, width: "100%" }}>
        {/* Décale le contenu sous la barre fixe, sans marge magique. */}
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
